import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthProvider';
import { useNavigate } from 'react-router-dom';

const MyOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true); // ✅ New loading state

  useEffect(() => {
    if (!user?.phone) return;

    const normalizedPhone = user.phone.startsWith('+964') ? user.phone : `+964${user.phone}`;

    console.log("Frontend sending phone:", normalizedPhone); // 🔥 Debug log clearly

    axios.get(`/api/direct-orders/my-orders/${normalizedPhone}`)
      .then(res => {
        setOrders(res.data);
      })
      .catch(err => {
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user]);


  if (!user) return (
    <div className="flex justify-center items-center h-96">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">You must be logged in</h2>
        <button
          className="bg-black text-white px-6 py-2 rounded hover:bg-gray-700"
          onClick={() => navigate('/auth')}
        >
          Go to Login
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500 text-xl">Loading your orders...</div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold">No orders yet</h2>
          <p className="text-gray-600 mt-2">Start shopping to place your first order!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">My Orders</h2>

{orders.map(order => {
  const firstProduct = (order.items && order.items[0]) || (order.products && order.products[0]);
  const imageUrl = firstProduct?.productFinalImage || firstProduct?.photo || firstProduct?.photos?.[0] || '';
  const fixedUrl = imageUrl.startsWith('uploads/') ? `${window.location.origin}/${imageUrl.replace(/\\/g, '/')}` : imageUrl;

  // Status color
  const statusColor = {
    Delivered: 'bg-green-100 text-green-700',
    Pending: 'bg-red-100 text-red-700',
    Processing: 'bg-yellow-100 text-yellow-800',
    Cancelled: 'bg-gray-200 text-gray-700',
    'Out for Delivery': 'bg-blue-100 text-blue-700',
  }[order.status] || 'bg-gray-100 text-gray-600';

  return (
<div key={order._id} className="bg-white rounded-xl p-5 mb-2 shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-gray-100">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left: Image */}
        <div className="flex-shrink-0">
          <img
            src={fixedUrl}
            alt="Product"
            className="w-20 h-20 object-cover rounded border"
            onError={(e) => (e.target.style.display = 'none')}
          />
        </div>

        {/* Middle: Order Info */}
        <div className="flex-1 text-sm sm:ml-4 space-y-1">
          <p className="text-gray-600">
            <span className="font-semibold">Order ID:</span> {order.orderId}
          </p>
          <p className="text-gray-600">
            <span className="font-semibold">Status:</span>{" "}
            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${statusColor}`}>
              {order.status}
            </span>
          </p>
          <p className="text-gray-600">
            <span className="font-semibold">Items:</span> {order.items?.length || order.products?.length}
          </p>
          <p className="text-gray-600">
            <span className="font-semibold">Date:</span> {new Date(order.createdAt).toLocaleDateString()}
          </p>
        </div>

        {/* Right: Button */}
        <div className="sm:ml-auto flex-shrink-0">
          <button
            className="bg-black text-white px-5 py-2 rounded hover:bg-gray-800 transition"
            onClick={() => setSelectedOrder(order)}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
})}

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md sm:max-w-3xl mx-4 sm:mx-0 overflow-y-auto max-h-[90vh] relative shadow-xl">
            <button className="absolute top-2 right-2 text-xl" onClick={() => setSelectedOrder(null)}>✖</button>
            <h3 className="text-2xl font-bold mb-4">Order Details</h3>

            {/* ✅ Progress Tracker */}
            <div className="relative flex items-center justify-between mb-6 px-1 sm:px-4" style={{ minHeight: 40 }}>
  {/* Connecting line */}
  <div className="absolute top-1/2 left-2 right-2 sm:left-4 sm:right-4 h-0.5 bg-gray-300 z-0" style={{ transform: "translateY(-50%)" }}></div>

  {['Pending', 'Processing', 'Delivery', 'Delivered'].map((step, idx, arr) => {
    const currentIndex = ['pending', 'processing', 'out for delivery', 'delivered']
      .indexOf(selectedOrder.status.toLowerCase());
    const isActive = idx <= currentIndex;

    return (
      <div key={step} className="flex flex-col items-center flex-1 z-10">
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[11px] font-bold
            ${isActive ? 'bg-green-600' : 'bg-gray-300'}`}
        >
          {idx + 1}
        </div>
        <span
          className={`mt-1 text-center text-[10px] sm:text-xs font-medium leading-tight
            ${isActive ? 'text-green-700' : 'text-gray-400'}`}
          style={{ minWidth: 48 }}
        >
          {step}
        </span>
      </div>
    );
  })}
</div>


            {/* ✅ Order Info */}
            <div className="text-sm mb-4">
              <p><strong>Order ID:</strong> {selectedOrder.orderId}</p>
              <p><strong>Phone:</strong> {selectedOrder.customer?.phone}</p>
              <p><strong>Address:</strong> {selectedOrder.customer?.address}</p>
              <p><strong>Status:</strong> {selectedOrder.status}</p>
            </div>

            <hr className="my-4" />

            {/* ✅ Order Items */}
            {(selectedOrder.items || selectedOrder.products)?.map((item, i) => {
              const imageUrl = item.productFinalImage || item.photo || item.photos?.[0] || '';
              const fixedUrl = imageUrl.startsWith('uploads/') ? `/uploads/${imageUrl.replace(/\\/g, '/')}` : imageUrl;

              return (
                <div key={i} className="flex flex-col sm:flex-row items-start gap-4 mb-4">
                  <img src={fixedUrl} alt="Product" className="w-20 h-20 object-cover border rounded" onError={(e) => e.target.style.display = 'none'} />
                  <div className="flex-1">
                    <p className="font-semibold">{item.name || item.modelName}</p>
                    <p className="text-sm text-gray-600">Size: {item.size}</p>
                    <p className="text-sm text-gray-600">Weight: {item.weight}g</p>
                    {item.finalWeight && <p className="text-sm text-gray-600">Final Weight: {item.finalWeight}g</p>}
                    <p className="text-sm text-gray-600">Guarantee: {item.guarantee}</p>
                    <p className="font-bold mt-1">${item.price?.toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      )}
    </div>
  );
};

export default MyOrdersPage;
